class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });

    if (typeof window.syncFreeGift === 'function') {
      window.applyFreeGiftState(parsedState.sections && parsedState.sections['cart-drawer']);
      window.syncFreeGift({ initial: true });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  connectedCallback() {
    if (typeof window.syncFreeGift === 'function') {
      window.syncFreeGift({ initial: true });
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function cartPageSectionTargets() {
  return ['main-cart-items', 'main-cart-footer']
    .map((id) => {
      const element = document.getElementById(id);
      if (!element || !element.dataset.id) return null;
      return { id, sectionId: element.dataset.id, selector: '.js-contents' };
    })
    .filter(Boolean);
}

function freeGiftSectionsPayload(extra) {
  const cartDrawer = document.querySelector('cart-drawer');
  const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
  cartPageSectionTargets().forEach((target) => sections.push(target.sectionId));
  return {
    ...extra,
    sections: sections.join(','),
    sections_url: window.location.pathname,
  };
}

function renderFreeGiftCart(parsedState) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer || !parsedState || !parsedState.sections) return;

  window.applyFreeGiftState(parsedState.sections['cart-drawer']);
  cartDrawer.getSectionsToRender().forEach((section) => {
    const sectionElement = section.selector
      ? document.querySelector(section.selector)
      : document.getElementById(section.id);
    const sectionHtml = parsedState.sections[section.id];
    if (!sectionElement || !sectionHtml) return;
    sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(sectionHtml, section.selector);
  });

  if (typeof parsedState.item_count === 'number') {
    cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
    const cartFooter = document.getElementById('main-cart-footer');
    const cartItems = document.querySelector('cart-items');
    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartItems) cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
  } else {
    cartDrawer.classList.remove('is-empty');
  }

  cartPageSectionTargets().forEach((target) => {
    const html = parsedState.sections[target.sectionId];
    const element = document.getElementById(target.id);
    if (!html || !element) return;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector);
    const destination = element.querySelector(target.selector);
    if (source && destination) destination.innerHTML = source.innerHTML;
  });

  const overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
  if (overlay) overlay.addEventListener('click', cartDrawer.close.bind(cartDrawer));
}

function isFreeGiftLine(item, variantId) {
  if (!item || String(item.variant_id) !== String(variantId)) return false;
  const properties = item.properties || {};
  return String(properties._free_gift) === 'true';
}

async function freeGiftCartRequest(url, body) {
  const response = await fetch(url, { ...fetchConfig('json'), body: JSON.stringify(body) });
  const parsed = await response.json();
  if (!response.ok || parsed.status) return null;
  return parsed;
}

let freeGiftSyncRunning = false;
let freeGiftSyncQueued = false;

window.syncFreeGift = async function syncFreeGift(options) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer || cartDrawer.dataset.freeGiftEnabled !== 'true') return;

  // The server-rendered drawer already knows whether the gift is due, so a
  // missing gift can be added straight away without fetching the cart first.
  let addDirectly = false;
  if (options && options.initial) {
    const shouldHaveGift = cartDrawer.dataset.freeGiftState === 'unlocked';
    const quantity = Number(cartDrawer.dataset.freeGiftQuantity || 0);
    const inSync = (shouldHaveGift && quantity === 1) || (!shouldHaveGift && quantity === 0);
    if (inSync) return;
    addDirectly = shouldHaveGift && quantity === 0;
  }

  if (freeGiftSyncRunning) {
    freeGiftSyncQueued = true;
    return;
  }

  freeGiftSyncRunning = true;
  try {
    if (addDirectly) {
      await addFreeGift(cartDrawer.dataset.freeGiftVariantId);
      return;
    }

    // A full cart from /cart/change saves a round trip to /cart.js.
    let cart = isFullCart(options && options.cart) ? options.cart : null;
    let guard = 0;
    while (guard < 4) {
      freeGiftSyncQueued = false;
      if (!cart) cart = await fetchFreeGiftCart();
      if (!cart) break;
      const result = await syncFreeGiftOnce(cartDrawer, cart);
      if (!result) break;
      cart = isFullCart(result) ? result : null;
      if (!cart) break;
      guard += 1;
    }
  } catch (error) {
    console.error(error);
  } finally {
    freeGiftSyncRunning = false;
    if (freeGiftSyncQueued) {
      freeGiftSyncQueued = false;
      window.syncFreeGift();
    }
  }
};

function isFullCart(cart) {
  return Boolean(cart && Array.isArray(cart.items) && typeof cart.total_price === 'number');
}

async function fetchFreeGiftCart() {
  const cartResponse = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!cartResponse.ok) return null;
  return cartResponse.json();
}

// Copies the server-computed gift state from freshly rendered drawer HTML onto
// the live <cart-drawer>, whose own attributes are never re-rendered.
window.applyFreeGiftState = function applyFreeGiftState(sectionHtml) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer || !sectionHtml) return;
  const source = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('cart-drawer');
  if (!source) return;
  ['freeGiftState', 'freeGiftQuantity'].forEach((key) => {
    if (source.dataset[key] !== undefined) cartDrawer.dataset[key] = source.dataset[key];
  });
};

function hideFreeGiftRows() {
  document.querySelectorAll('.cart-item--free-gift').forEach((row) => {
    row.style.display = 'none';
  });
}

async function addFreeGift(variantId) {
  if (!variantId) return null;
  const added = await freeGiftCartRequest(
    routes.cart_add_url,
    freeGiftSectionsPayload({
      items: [{ id: Number(variantId), quantity: 1, properties: { _free_gift: 'true' } }],
    })
  );
  if (added) renderFreeGiftCart(added);
  return added;
}

// Returns the cart request's response when something changed, otherwise null.
async function syncFreeGiftOnce(cartDrawer, cart) {
  const variantId = cartDrawer.dataset.freeGiftVariantId;
  const threshold = Number(cartDrawer.dataset.freeGiftThreshold);
  if (!variantId || !threshold) return null;

  const gifts = cart.items.filter((item) => isFreeGiftLine(item, variantId));
  const giftTotal = gifts.reduce((sum, item) => sum + item.final_line_price, 0);
  const qualifying = Math.max(0, cart.total_price - giftTotal);
  const requiredProducts = (cartDrawer.dataset.freeGiftRequiredProducts || '').split(',').filter(Boolean);
  const eligible =
    !requiredProducts.length ||
    cart.items.some(
      (item) => !isFreeGiftLine(item, variantId) && requiredProducts.includes(String(item.product_id))
    );
  const unlocked = eligible && qualifying >= threshold;

  if (!unlocked) {
    if (!gifts.length) return null;
    if (typeof setCheckoutLocked === 'function') setCheckoutLocked(true);
    // Hide the gift immediately so it doesn't linger while the request runs.
    hideFreeGiftRows();
    const removed = await freeGiftCartRequest(
      routes.cart_change_url,
      freeGiftSectionsPayload({ id: gifts[0].key, quantity: 0 })
    );
    if (removed) renderFreeGiftCart(removed);
    return removed;
  }

  if (!gifts.length) return addFreeGift(variantId);

  if (gifts.length > 1 || gifts[0].quantity !== 1) {
    const updated = await freeGiftCartRequest(
      routes.cart_change_url,
      freeGiftSectionsPayload({ id: gifts[0].key, quantity: gifts.length > 1 ? 0 : 1 })
    );
    if (updated) renderFreeGiftCart(updated);
    return updated;
  }

  return null;
}

if (document.querySelector('cart-drawer')) {
  window.syncFreeGift({ initial: true });
}
